<template>
  <div
    v-if="isVisible"
    ref="dialogRef"
    class="v3-action-dialog text-left"
    :class="{ danger }"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="dialogLabelledBy"
    :aria-describedby="dialogDescriptionId"
    :aria-label="dialogAriaLabel"
    tabindex="-1"
  >
    <div class="dialog-header">
      <h2
        v-if="props.title"
        :id="dialogTitleId"
        class="dialog-title pe-3 lh-normal"
      >
        {{ title }}
      </h2>
      <button
        type="button"
        class="close-dialog"
        :aria-label="resolvedCloseButtonAriaLabel"
        @click="close"
      >
        <i class="ri-close-line" aria-hidden="true"></i>
      </button>
    </div>
    <div :id="dialogDescriptionId" class="dialog-content mb-3">
      <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {{ liveAnnouncement }}
      </div>
      <div v-if="question">{{ question }}</div>
      <slot></slot>
    </div>
    <div class="dialog-actions">
      <PillButton
        data-test-id="actions-dialog-confirm"
        type="button"
        @click="confirm"
        active
        rounded
        prepend-icon="ri-check-line"
        class="solid me-2"
        :class="{ danger }"
        :aria-label="confirmButtonAriaLabel"
        >{{ props.confirmText }}</PillButton
      >
      <PillButton
        type="button"
        @click="close"
        :class="{ danger }"
        rounded
        prepend-icon="ri-close-line"
        :aria-label="cancelButtonAriaLabel"
        >Cancel</PillButton
      >
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onUnmounted, ref, watch } from "vue"
import PillButton from "../Buttons/PillButton.vue"
import { setPassDialogOpen } from "@/helpers/accessibeModalGuard"

let actionDialogIdCounter = 0

export default {
  props: {
    title: {
      type: String,
      default: ""
    },
    question: {
      type: String,
      default: ""
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    danger: {
      type: Boolean,
      default: false
    },
    confirmText: {
      type: String,
      default: "Confirm"
    },
    closeButtonAriaLabel: {
      type: String,
      default: "Close dialog"
    }
  },
  emits: ["close", "confirm"],
  components: { PillButton },
  setup(props, { emit }) {
    const dialogRef = ref(null)
    const lastFocusedElement = ref(null)
    let accessibeDialogHeld = false

    const holdAccessibeDialog = (shouldHold) => {
      if (shouldHold && !accessibeDialogHeld) {
        setPassDialogOpen(true)
        accessibeDialogHeld = true
        return
      }
      if (!shouldHold && accessibeDialogHeld) {
        setPassDialogOpen(false)
        accessibeDialogHeld = false
      }
    }
    const liveAnnouncement = ref("")
    const dialogId = `action-dialog-${++actionDialogIdCounter}`
    const dialogTitleId = `${dialogId}-title`
    const dialogDescriptionId = `${dialogId}-description`

    const dialogAriaLabel = computed(() =>
      props?.title ? undefined : "Action confirmation dialog"
    )
    const dialogLabelledBy = computed(() =>
      props?.title ? dialogTitleId : undefined
    )
    const dialogContextLabel = computed(() => {
      const titleText = props?.title?.trim?.() || ""
      if (titleText) return titleText

      const questionText = props?.question?.trim?.() || ""
      if (questionText) return questionText

      return ""
    })
    const confirmButtonAriaLabel = computed(() => {
      const confirmText = props?.confirmText?.trim?.() || "Confirm"
      const contextLabel = dialogContextLabel.value

      return contextLabel
        ? `${confirmText}: ${contextLabel}`
        : `${confirmText} action`
    })
    const cancelButtonAriaLabel = computed(() => {
      const contextLabel = dialogContextLabel.value
      return contextLabel ? `Cancel: ${contextLabel}` : "Cancel action"
    })
    const resolvedCloseButtonAriaLabel = computed(() => {
      const customCloseLabel = props?.closeButtonAriaLabel?.trim?.() || ""
      if (customCloseLabel && customCloseLabel !== "Close dialog") {
        return customCloseLabel
      }

      const contextLabel = dialogContextLabel.value
      return contextLabel ? `Close dialog: ${contextLabel}` : "Close dialog"
    })

    const getFocusableElements = () => {
      if (!dialogRef.value) return []

      return [
        ...dialogRef.value.querySelectorAll(
          "button,[href],input,select,textarea,[tabindex]:not([tabindex='-1'])"
        )
      ].filter((element) => {
        return (
          !element.hasAttribute("disabled") &&
          element.getAttribute("aria-hidden") !== "true"
        )
      })
    }

    const focusFirstFocusableElement = () => {
      const focusableElements = getFocusableElements()
      const firstElement = focusableElements[0]

      if (firstElement?.focus) {
        firstElement.focus()
        return
      }

      dialogRef.value?.focus?.()
    }

    const trapTabNavigation = (event) => {
      if (!props.isVisible || event.key !== "Tab") return

      const focusableElements = getFocusableElements()
      if (!focusableElements.length) {
        event.preventDefault()
        dialogRef.value?.focus?.()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const activeElement = document.activeElement
      const isInsideDialog = dialogRef.value?.contains(activeElement)

      if (!isInsideDialog) {
        event.preventDefault()
        ;(event.shiftKey ? lastElement : firstElement).focus()
        return
      }

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
        return
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    const closeOnEscape = (event) => {
      if (!props.isVisible || event.key !== "Escape") return
      event.preventDefault()
      close()
    }

    const addFocusTrapListeners = () => {
      document.addEventListener("keydown", trapTabNavigation, true)
      document.addEventListener("keydown", closeOnEscape, true)
    }

    const removeFocusTrapListeners = () => {
      document.removeEventListener("keydown", trapTabNavigation, true)
      document.removeEventListener("keydown", closeOnEscape, true)
    }

    watch(
      () => props.isVisible,
      async (isVisible) => {
        holdAccessibeDialog(isVisible)
        if (isVisible) {
          lastFocusedElement.value = document.activeElement
          addFocusTrapListeners()
          await nextTick()
          liveAnnouncement.value = dialogContextLabel.value
            ? `Dialog opened. ${dialogContextLabel.value}`
            : "Dialog opened."
          focusFirstFocusableElement()
          return
        }

        liveAnnouncement.value = ""
        removeFocusTrapListeners()
        lastFocusedElement.value?.focus?.()
      },
      { immediate: true }
    )

    onUnmounted(() => {
      removeFocusTrapListeners()
      holdAccessibeDialog(false)
    })

    const close = () => {
      removeFocusTrapListeners()
      emit("close")
    }
    const confirm = () => {
      removeFocusTrapListeners()
      emit("confirm")
    }
    return {
      close,
      confirm,
      props,
      dialogAriaLabel,
      dialogRef,
      dialogTitleId,
      dialogDescriptionId,
      dialogLabelledBy,
      confirmButtonAriaLabel,
      cancelButtonAriaLabel,
      resolvedCloseButtonAriaLabel,
      liveAnnouncement
    }
  }
}
</script>

<style></style>
